import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import dayjs from "dayjs";

import { apiClient } from "../../services/api/apiClient";
import { usePageTitle } from "../../hooks";
import { PageHeader } from "../../components/common/PageHeader";
import { AuditCard } from "./components/AuditCard";
import { TodayMap } from "./components/TodayMap";
import type {
  DashboardAuditCard,
  DashboardPayload,
  DashboardTeamRow,
} from "./types";

const { Text } = Typography;

/**
 * Per-role dashboard. Consumes `/api/v1/dashboard/` (see backend
 * docstring for the payload shape). Renders the panels that the
 * backend chose to include for the caller's role — every panel
 * gates its own block on `payload.<key>` being present, so adding
 * panels later is purely additive.
 */
export const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  usePageTitle(t("dashboard.title", "Dashboard"));

  const [payload, setPayload] = useState<DashboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<DashboardPayload>("/v1/dashboard/");
      setPayload(res.data);
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        t("dashboard.load_error", "No se pudo cargar el dashboard");
      setError(detail);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // No deps: the endpoint already filters by request.user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const todayWindowLabel = useMemo(() => {
    if (!payload?.today_window) return null;
    const { from, to } = payload.today_window;
    return `${dayjs(from).format("DD/MM/YYYY")} – ${dayjs(to).format("DD/MM/YYYY")}`;
  }, [payload?.today_window]);

  return (
    <div style={{ padding: "0 8px" }}>
      <PageHeader
        icon={<ThunderboltOutlined />}
        title={t("dashboard.title", "Dashboard")}
        subtitle={
          payload
            ? t(
                "dashboard.subtitle",
                "Resumen del día y atajos a tus auditorías",
              )
            : ""
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchDashboard}
            loading={loading}
            style={{ borderRadius: 999, paddingInline: 16 }}
          >
            {t("common.refresh", "Refrescar")}
          </Button>
        }
      />

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error}
        />
      )}

      {loading && !payload ? (
        <div style={{ padding: 64, textAlign: "center" }}>
          <Spin size="large" />
        </div>
      ) : !payload ? (
        <Empty
          description={t(
            "dashboard.empty",
            "El dashboard aún no está disponible para tu perfil.",
          )}
        />
      ) : (
        <>
          {/* KPI row */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {payload.kpis.map((k) => (
              <Col xs={12} sm={12} md={6} key={k.key}>
                <Card size="small">
                  <Statistic title={k.label} value={k.value} />
                </Card>
              </Col>
            ))}
          </Row>

          {/* Today panel + mini-map */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} lg={14}>
              <Card
                size="small"
                title={
                  <Space>
                    <CalendarOutlined style={{ color: "#1677ff" }} />
                    <span>
                      {t("dashboard.today", "Hoy")}
                      {todayWindowLabel && (
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                          {todayWindowLabel}
                        </Text>
                      )}
                    </span>
                  </Space>
                }
              >
                {payload.today_audits.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t(
                      "dashboard.today_empty",
                      "Sin auditorías en la ventana de hoy",
                    )}
                  />
                ) : (
                  payload.today_audits.map((a) => (
                    <AuditCard
                      key={a.id}
                      audit={a}
                      hideAuditor={payload.role === "auditor"}
                    />
                  ))
                )}
              </Card>
            </Col>
            <Col xs={24} lg={10}>
              <Card
                size="small"
                title={
                  <Space>
                    <EnvironmentOutlined style={{ color: "#52c41a" }} />
                    {t("dashboard.today_map", "Mapa de hoy")}
                  </Space>
                }
              >
                <TodayMap audits={payload.today_audits} height={360} />
              </Card>
            </Col>
          </Row>

          {/* Auditor-only panels */}
          {payload.role === "auditor" && (
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} lg={12}>
                <PanelList
                  title={t("dashboard.in_progress", "En curso")}
                  icon={<ThunderboltOutlined style={{ color: "#faad14" }} />}
                  audits={payload.in_progress ?? []}
                  hideAuditor
                  emptyMessage={t(
                    "dashboard.in_progress_empty",
                    "Ninguna auditoría iniciada sin cerrar",
                  )}
                />
              </Col>
              <Col xs={24} lg={12}>
                <PanelList
                  title={t("dashboard.upcoming", "Próximos días")}
                  icon={<CalendarOutlined style={{ color: "#1677ff" }} />}
                  audits={payload.upcoming ?? []}
                  hideAuditor
                  emptyMessage={t(
                    "dashboard.upcoming_empty",
                    "Sin auditorías planificadas en los próximos 7 días",
                  )}
                />
              </Col>
            </Row>
          )}

          {/* Coordinator- y Eurosemillas-shared panels: sin asignar +
              atrasadas + long-running. El coordinador ve el bloque
              scopeado por su audit_company; el ESA por su portfolio
              country — el backend ya envía la lista correcta en cada
              caso, aquí solo pintamos. */}
          {(payload.role === "coordinator" || payload.role === "esa") && (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} lg={8}>
                  <PanelList
                    title={t("dashboard.unassigned", "Sin asignar")}
                    icon={
                      <WarningOutlined style={{ color: "#fa541c" }} />
                    }
                    audits={payload.unassigned ?? []}
                    emptyMessage={t(
                      "dashboard.unassigned_empty",
                      "Todas las auditorías tienen auditor",
                    )}
                  />
                </Col>
                <Col xs={24} lg={8}>
                  <PanelList
                    title={t("dashboard.late_planned", "Atrasadas")}
                    icon={
                      <WarningOutlined style={{ color: "#fa8c16" }} />
                    }
                    audits={payload.stuck?.late_planned ?? []}
                    emptyMessage={t(
                      "dashboard.late_planned_empty",
                      "Ninguna auditoría atrasada",
                    )}
                  />
                </Col>
                <Col xs={24} lg={8}>
                  <PanelList
                    title={t(
                      "dashboard.long_running",
                      "Iniciadas hace > 7 días",
                    )}
                    icon={
                      <WarningOutlined style={{ color: "#cf1322" }} />
                    }
                    audits={payload.stuck?.long_running ?? []}
                    emptyMessage={t(
                      "dashboard.long_running_empty",
                      "Sin auditorías iniciadas hace más de 7 días",
                    )}
                  />
                </Col>
              </Row>

              {payload.team && (
                <Card
                  size="small"
                  title={
                    <Space>
                      <TeamOutlined style={{ color: "#722ed1" }} />
                      {t("dashboard.team", "Mi equipo (esta campaña)")}
                    </Space>
                  }
                  style={{ marginBottom: 16 }}
                >
                  <Table<DashboardTeamRow>
                    size="small"
                    rowKey="auditor_user_id"
                    dataSource={payload.team}
                    pagination={false}
                    columns={[
                      {
                        title: t("audit.auditor", "Auditor"),
                        dataIndex: "auditor_name",
                      },
                      {
                        title: t("dashboard.audits", "Auditorías"),
                        align: "center",
                        render: (_: unknown, r: DashboardTeamRow) => (
                          <span>
                            <strong>{r.audits_realized}</strong>
                            <span style={{ color: "#8c8c8c" }}>
                              {" "}
                              / {r.audits_total}
                            </span>
                          </span>
                        ),
                      },
                      {
                        title: t("dashboard.progress", "% completado"),
                        render: (_: unknown, r: DashboardTeamRow) => {
                          const total = r.audits_total || 0;
                          if (total === 0) return "—";
                          const pct = Math.round(
                            (r.audits_realized / total) * 100,
                          );
                          return (
                            <Progress
                              percent={pct}
                              size="small"
                              strokeColor={
                                pct >= 100
                                  ? "#52c41a"
                                  : pct >= 50
                                    ? "#13c2c2"
                                    : "#faad14"
                              }
                            />
                          );
                        },
                      },
                      {
                        title: t(
                          "dashboard.last_activity",
                          "Última actividad",
                        ),
                        dataIndex: "last_activity",
                        render: (v: string | null) =>
                          v ? dayjs(v).format("DD/MM/YYYY") : "—",
                      },
                    ]}
                  />
                </Card>
              )}
            </>
          )}

          {/* Incidences (shared between roles) */}
          {payload.open_incidences && (
            <Card
              size="small"
              title={
                <Space>
                  <WarningOutlined style={{ color: "#faad14" }} />
                  {payload.role === "coordinator"
                    ? t(
                        "dashboard.open_incidences_coord",
                        "Incidencias asignadas a mí",
                      )
                    : t(
                        "dashboard.open_incidences_auditor",
                        "Mis incidencias abiertas",
                      )}
                </Space>
              }
            >
              {payload.open_incidences.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t(
                    "dashboard.no_incidences",
                    "Sin incidencias abiertas",
                  )}
                />
              ) : (
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={payload.open_incidences}
                  pagination={false}
                  onRow={(record) => ({
                    onClick: () => navigate(`/incidences/${record.id}`),
                    style: { cursor: "pointer" },
                  })}
                  columns={[
                    {
                      title: t("incidence.num_short", "Nº"),
                      dataIndex: "code",
                      width: 110,
                      render: (v: string) => <strong>{v || "—"}</strong>,
                    },
                    {
                      title: t("incidence.subject", "Asunto"),
                      dataIndex: "subject",
                      ellipsis: true,
                    },
                    {
                      title: t("incidence.type_short", "Tipo"),
                      dataIndex: "category_name",
                      width: 150,
                      ellipsis: true,
                    },
                    {
                      title: t("incidence.priority_label", "Prioridad"),
                      dataIndex: "priority",
                      width: 110,
                      render: (v: string) => <Tag>{v}</Tag>,
                    },
                    {
                      title: t("incidence.created", "Fecha"),
                      dataIndex: "created",
                      width: 110,
                      render: (v: string | null) =>
                        v ? dayjs(v).format("DD/MM/YYYY") : "—",
                    },
                  ]}
                />
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
};

/** Small wrapper used by both role layouts — Card with title +
 *  a vertical list of `AuditCard`s, or an Empty placeholder. */
interface PanelListProps {
  title: string;
  icon?: React.ReactNode;
  audits: DashboardAuditCard[];
  emptyMessage: string;
  hideAuditor?: boolean;
}
const PanelList = ({
  title,
  icon,
  audits,
  emptyMessage,
  hideAuditor,
}: PanelListProps) => (
  <Card
    size="small"
    title={
      <Space>
        {icon}
        {title}
      </Space>
    }
    style={{ height: "100%" }}
  >
    {audits.length === 0 ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyMessage} />
    ) : (
      audits.map((a) => (
        <AuditCard key={a.id} audit={a} hideAuditor={hideAuditor} />
      ))
    )}
  </Card>
);

export default Dashboard;
